/**
 * Template de prise de besoin : Plan d'incident majeur v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Plan spécifique pour la gestion des incidents majeurs (P1/CAT 1)
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanIncidentMajeurV1: IntakeTemplate = {
  id: 'plan-incident-majeur-v1',
  name: 'Prise de besoin Plan d\'incident majeur v1',
  description:
    'Questionnaire complet pour élaborer un plan d\'incident majeur conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit les procédures d\'activation, l\'équipe d\'intervention, les workflows critiques et les processus de communication d\'urgence.',
  documentTypeCode: 'MAJOR_INCIDENT_PLAN',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Définition & Activation',
      description: 'Critères d\'incident majeur et procédures d\'activation',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le plan d\'incident majeur.',
        },
        {
          id: 'criteres_incident_majeur',
          label: 'Quels sont les critères d\'un incident majeur (P1/CAT 1) ?',
          type: 'textarea',
          placeholder: 'Ex: Service critique complètement indisponible, plus de 50 utilisateurs affectés, impact financier > 50 000$, violation de sécurité critique...',
          required: true,
          helpText: 'ITIL 4: Critères clairs pour activation du plan d\'incident majeur.',
        },
        {
          id: 'qui_peut_activer',
          label: 'Qui peut activer le plan d\'incident majeur ?',
          type: 'multiselect',
          options: [
            'Gestionnaire d\'incidents',
            'Responsable sécurité',
            'Directeur TI',
            'Directeur général',
            'Tout membre de l\'équipe TI',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Autorités d\'activation pour réponse rapide.',
        },
        {
          id: 'processus_activation',
          label: 'Quel est le processus d\'activation ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: détection → évaluation → décision activation → notification équipe → mobilisation...',
          helpText: 'ITIL 4: Processus structuré pour activation rapide.',
        },
        {
          id: 'ton_document_plan',
          label: 'Quel ton souhaitez-vous pour ce plan ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation en situation d\'urgence.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Équipe d\'Intervention Majeure',
      description: 'Composition et rôles de l\'équipe d\'intervention',
      icon: 'Users',
      questions: [
        {
          id: 'composition_equipe',
          label: 'Qui compose l\'équipe d\'intervention majeure ?',
          type: 'multiselect',
          options: [
            'Commandant d\'incident',
            'Responsable technique',
            'Responsable communication',
            'Responsable juridique',
            'Experts techniques',
            'Direction',
            'Relations publiques',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Équipe structurée pour gestion efficace des incidents majeurs.',
        },
        {
          id: 'commandant_incident',
          label: 'Qui est le commandant d\'incident (Incident Commander) ?',
          type: 'text',
          placeholder: 'Ex: Directeur TI, Responsable Cybersécurité...',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Commandant d\'incident pour coordination centrale.',
        },
        {
          id: 'responsabilites_equipe',
          label: 'Quelles sont les responsabilités de chaque rôle ?',
          type: 'textarea',
          placeholder: 'Décrivez les responsabilités: Commandant → coordination, Responsable technique → résolution, Responsable communication → communication...',
          helpText: 'ITIL 4: Responsabilités claires pour éviter les conflits.',
        },
        {
          id: 'contact_urgence_24_7',
          label: 'Quels sont les contacts d\'urgence 24/7 pour l\'équipe ?',
          type: 'textarea',
          placeholder: 'Listez les contacts avec numéros de téléphone, courriels, etc.',
          helpText: 'ITIL 4: Contacts d\'urgence essentiels pour mobilisation rapide.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Workflows Critiques',
      description: 'Workflows et procédures critiques pour incidents majeurs',
      icon: 'FileCheck',
      questions: [
        {
          id: 'workflow_resolution',
          label: 'Quel est le workflow de résolution d\'un incident majeur ?',
          type: 'textarea',
          placeholder: 'Décrivez le workflow: Activation → Mobilisation → Analyse → Confinement → Éradication → Restauration → Fermeture...',
          helpText: 'ITIL 4: Workflow structuré pour résolution efficace.',
        },
        {
          id: 'escalade_incident_majeur',
          label: 'Quel est le processus d\'escalade pour un incident majeur ?',
          type: 'textarea',
          placeholder: 'Décrivez les niveaux d\'escalade: Équipe → Gestionnaire → Direction → Comité de crise...',
          helpText: 'ITIL 4: Escalade structurée pour incidents majeurs.',
        },
        {
          id: 'decision_rapide',
          label: 'Comment sont prises les décisions rapides en situation d\'urgence ?',
          type: 'select',
          options: [
            'Commandant d\'incident décide seul',
            'Consultation rapide équipe (15 min)',
            'Consultation direction (30 min)',
            'Comité de crise',
            'Autre',
          ],
          helpText: 'ITIL 4: Processus de décision pour réponse rapide.',
        },
        {
          id: 'outils_intervention',
          label: 'Quels outils sont utilisés pour l\'intervention ?',
          type: 'multiselect',
          options: [
            'Système de ticketing',
            'Outils de communication (Teams, Slack)',
            'Outils de monitoring',
            'Outils forensiques',
            'Outils de restauration',
            'Autre',
          ],
          helpText: 'ITIL 4: Outils appropriés pour intervention efficace.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Communication d\'Urgence & Post-Incident',
      description: 'Communication d\'urgence et activités post-incident',
      icon: 'MessageSquare',
      questions: [
        {
          id: 'communication_urgence',
          label: 'Comment communique-t-on lors d\'un incident majeur ?',
          type: 'multiselect',
          options: [
            'Réunion d\'urgence immédiate',
            'Canal de communication dédié',
            'Mises à jour horaires',
            'Communication clients',
            'Communication publique',
            'Communication réglementaire',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Communication d\'urgence essentielle.',
        },
        {
          id: 'templates_communication',
          label: 'Quels templates de communication sont utilisés ?',
          type: 'textarea',
          placeholder: 'Décrivez les templates: notification interne, communication clients, communication publique...',
          helpText: 'NIST CSF 2.0 (RS.CO): Templates pour communication cohérente.',
        },
        {
          id: 'activites_post_incident',
          label: 'Quelles activités post-incident sont prévues ?',
          type: 'multiselect',
          options: [
            'Rapport d\'incident majeur',
            'Analyse de cause racine approfondie',
            'Leçons apprises',
            'Amélioration des procédures',
            'Formation supplémentaire',
            'Mise à jour du plan',
            'Autre',
          ],
          helpText: 'ITIL 4: Activités post-incident pour amélioration continue.',
        },
        {
          id: 'revision_plan',
          label: 'À quelle fréquence souhaitez-vous réviser ce plan ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





